import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff } from "lucide-react";
import AuthLayout from "@/components/layouts/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const loginSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(8, { message: "Password must be at least 8 characters" }),
  rememberMe: z.boolean().default(false),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const Login = () => {
  const { login, loginWithProvider } = useAuth();
  const { toast } = useToast();
  const { trackFormSubmission, trackButtonClick } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);
    try {
      await login(values.email, values.password);
      trackFormSubmission('login', true);
    } catch (error) {
      console.error("Login error in Login page:", error);
      trackFormSubmission('login', false);
      if (!error) {
        toast({
          title: "Login error",
          description: "An unexpected error occurred during login",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      trackButtonClick('Google Login', 'login');
      await loginWithProvider('google');
    } catch (error) {
      toast({
        title: "Login failed",
        description: error instanceof Error ? error.message : "Failed to login with Google",
        variant: "destructive",
      });
    }
  };

  return (
    <AuthLayout>
      <div className="flex min-h-screen items-center justify-center px-4 bg-white">
        <div className="w-full max-w-[621px] mt-[20px] md:mt-[72px] shadow-md p-8 md:p-[56px] rounded-md mb-16">
          <div className="text-center mb-8">
            <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist ">
              Welcome back to Wabisabi!
            </h1>
            <p className="text-[#666666] text-base text-[14px] md:text-[16px] font-normal">
              Enter your details to get started!
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Email"
                        className="h-[56px] px-4 text-[14px] md:text-base border-gray-200"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          className="h-[56px] px-4 pr-12 text-[14px] md:text-base border-gray-200"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-between">
                <FormField
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="data-[state=checked]:bg-[#6C4F77] data-[state=checked]:border-[#6C4F77]"
                        />
                      </FormControl>
                      <Label
                        htmlFor="remember-me"
                        className="text-sm font-normal text-[#666666] cursor-pointer"
                      >
                        Remember me
                      </Label>
                    </FormItem>
                  )}
                />

                <Link
                  to="/forgot-password"
                  className="text-sm text-[#6C4F77] hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#6C4F77] hover:bg-[#5A3A68] text-white font-bold text-[14px] font-modernist !mt-[48px] py-[20px]"
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </Form>

          <div className="mt-[19px] md:mt-[48px]">
            <div className="relative mb-[19px] md:mb-[48px]">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-[53px] text-gray-500">Or login with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full py-[16px] border-gray-200 hover:bg-gray-50 text-[14px]"
            >
              <img
                src="https://www.google.com/favicon.ico"
                alt="Google"
                className="w-5 h-5 mr-1"
              />
              <span className="font-normal text-[#141414]">Google</span>
            </Button>
          </div>

          <p className="mt-[16px] text-sm text-[#666666]">
            Don't have an account?{" "}
            <Link to="/signup" className="text-[#6C4F77] hover:underline font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;
